<template>
  <div>
    <b-modal id="modal-retry-confirm" hide-footer ref="retryConfirm" title="Confirm Retry">
      <b-row>
        <b-col>
          <p class="text-sm">
            <span class="h6">Are you sure you want to retry certification for this document?</span><br/>
            Tracking number <b class="text-blue">{{ trackingNumber }}</b>
            <span v-if="retryDocumentLabel"> — <b>{{ retryDocumentLabel }}</b></span>.
          </p>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col cols="4">
          <button class="px-3 py-2 border-2 border-blue bg-white rounded-sm text-blue w-full" @click="cancelRetry">
            <i class="fa fa-minus-square text-blue mr-2"></i>Cancel
          </button>
        </b-col>
        <b-col cols="4"></b-col>
        <b-col cols="4">
          <button class="px-3 py-2 bg-green rounded-sm text-white w-full hover:bg-green-dark" @click="confirmRetry" :disabled="loadingRetry">
            <i class="fa fa-check-circle text-white mr-1"></i> Confirm
            <b-spinner v-if="loadingRetry" small></b-spinner>
          </button>
        </b-col>
      </b-row>
    </b-modal>

    <b-overlay :show="loading" rounded="sm">
      <div v-if="details">
        <table class="min-w-full leading-normal mt-1 text-gray" style="border: solid 1px #dedede;">
          <thead>
            <tr class="bg-gray-pale">
              <td colspan="4" class="px-2 py-2">Certification Details</td>
            </tr>
            <tr>
              <th class="px-2 py-1">Tracking Number</th>
              <th class="px-2 py-1">Status</th>
              <th class="px-2 py-1">Document Type</th>
              <th class="px-2 py-1">Search Reference</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border: solid 1px #dedede;">
              <td class="px-2 py-1">{{ details.trackingNumber || trackingNumber }}</td>
              <td class="px-2 py-1 text-capitalize">{{ details.status ? details.status.toLowerCase() : "-" }}</td>
              <td class="px-2 py-1 text-capitalize">{{ cleanEntityType(details.documentType || "-") }}</td>
              <td class="px-2 py-1">{{ details.search || "-" }}</td>
            </tr>
          </tbody>
        </table>

        <table
          class="min-w-full leading-normal mt-3 text-gray"
          style="border: solid 1px #dedede;"
        >
          <thead>
            <tr class="bg-gray-pale">
              <td colspan="3" class="px-2 py-2">Payment Information</td>
            </tr>
            <tr>
              <th class="px-2 py-1">PRN</th>
              <th class="px-2 py-1">Payment Mode</th>
              <th class="px-2 py-1">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border: solid 1px #dedede;">
              <td class="px-2 py-1">{{ details.prn || "-" }}</td>
              <td class="px-2 py-1">{{ details.paymentMode ? details.paymentMode.replace(/_/g, " ") : "-" }}</td>
              <td class="px-2 py-1">{{ details.amount ? Number(details.amount).toLocaleString() : "-" }}</td>
            </tr>
          </tbody>
        </table>

        <table
          class="min-w-full leading-normal mt-3 text-blue-dark"
          style="border: solid 1px #bdd7f1;"
          v-if="details.applicant"
        >
          <thead>
            <tr class="bg-blue-pale">
              <td colspan="5" class="px-2 py-2">Applicant Details</td>
            </tr>
            <tr>
              <th class="px-2 py-1">Name</th>
              <th class="px-2 py-1">Email</th>
              <th class="px-2 py-1">Phone</th>
              <th class="px-2 py-1">ID Number</th>
              <th class="px-2 py-1">Account Type</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border: solid 1px #bdd7f1;">
              <td class="px-2 py-1 text-capitalize">
                {{ details.applicant.firstName }} <span v-if="details.applicant.lastName">{{ details.applicant.lastName }}</span>
              </td>
              <td class="px-2 py-1">{{ details.applicant.email || "-" }}</td>
              <td class="px-2 py-1">{{ details.applicant.phoneNumber || "-" }}</td>
              <td class="px-2 py-1">{{ details.applicant.idNumber || "-" }}</td>
              <td class="px-2 py-1 text-capitalize">{{ cleanEntityType(details.applicant.accountType || "-") }}</td>
            </tr>
          </tbody>
        </table>

        <b-row class="mx-1 mt-3">
          <b-col cols="12">
            <p class="px-2 py-1 text-xs font-bold mb-1 text-gray">
              Certified Documents ({{ (details.documents || []).length }})
            </p>
            <div v-if="details.documents && details.documents.length">
              <div
                v-for="(doc, idx) in details.documents"
                :key="doc.searchRequestDocumentId || doc.no || idx"
                class="p-2 mb-2"
                style="border: solid 1px #dedede;"
              >
                <b-row>
                  <b-col cols="3">
                    <small class="text-gray">Document Type</small>
                    <div class="font-bold text-capitalize">{{ cleanEntityType(doc.documentType || "-") }}</div>
                  </b-col>
                  <b-col cols="3">
                    <small class="text-gray">Instrument No.</small>
                    <div class="font-bold">{{ doc.no || "-" }}</div>
                  </b-col>
                  <b-col cols="3">
                    <small class="text-gray">Filing Date</small>
                    <div class="font-bold">{{ doc.filingDate ? dateFormat(doc.filingDate) : "-" }}</div>
                  </b-col>
                  <b-col cols="3" class="d-flex align-items-end justify-content-end">
                    <button
                      type="button"
                      class="text-xs bg-red text-white px-2 py-1 rounded-sm"
                      :disabled="loadingRetry && retryDocumentIdx === idx"
                      @click="retryPrompt(doc, idx)"
                    >
                      <i class="fa fa-redo mx-1"></i>Retry
                      <b-spinner v-if="loadingRetry && retryDocumentIdx === idx" small></b-spinner>
                    </button>
                  </b-col>
                </b-row>
                <b-row class="mt-1" v-if="doc.subjectMatter">
                  <b-col cols="12">
                    <small class="text-gray">Subject Matter</small>
                    <div>{{ doc.subjectMatter }}</div>
                  </b-col>
                </b-row>
                <b-row class="mt-2" v-if="documentParties(doc).length">
                  <b-col cols="12">
                    <small class="text-gray">Parties</small>
                    <table class="min-w-full leading-normal mt-1">
                      <thead>
                        <tr class="border-b border-t border-grey">
                          <th class="px-2 py-1">Name</th>
                          <th class="px-2 py-1">Type</th>
                          <th class="px-2 py-1">Identifier</th>
                          <th class="px-2 py-1">Contact</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(party, pIdx) in documentParties(doc)" :key="pIdx" class="border-t border-grey">
                          <td class="px-2 py-1 text-capitalize">{{ party.name }}</td>
                          <td class="px-2 py-1">{{ party.partyType }}</td>
                          <td class="px-2 py-1">{{ party.identifier }}</td>
                          <td class="px-2 py-1">{{ party.contact }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </b-col>
                </b-row>
              </div>
            </div>
            <p class="text-xs text-gray" v-else>No certified documents attached.</p>
          </b-col>
        </b-row>

        <p class="text-xs text-gray mt-2" v-if="details.message || details.reason">
          <span class="font-bold">Remarks:</span> {{ details.message || details.reason }}
        </p>
      </div>
      <div v-else-if="!loading" class="text-center text-sm text-gray py-3">
        No details found for this certification.
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { api } from "@/app/services/api.service";
import moment from "moment";

export default {
  name: "LegalDocumentCertificationDetails",
  props: {
    trackingNumber: String,
  },
  data() {
    return {
      loading: false,
      details: null,
      loadingRetry: false,
      retryDocumentIdx: null,
      retrySearchDocumentId: null,
      retryDocumentLabel: null,
    };
  },
  mounted() {
    this.fetch();
  },
  methods: {
    async fetch() {
      if (!this.trackingNumber) return;
      this.loading = true;
      try {
        const response = await api().get(
          "legal-documents/certification/details?trackingNumber=" + encodeURIComponent(this.trackingNumber)
        );
        const responseData = response ? response.data : null;
        this.details = responseData ? (responseData.data || responseData) : null;
      } catch (error) {
        this.details = null;
        if (error.response && error.response.data && error.response.data.message) {
          this.errorToast("Error", error.response.data.message);
        } else {
          this.errorToast("Error", "Failed to load certification details");
        }
      } finally {
        this.loading = false;
      }
    },
    dateFormat(date) {
      return moment(date).format("DD-MM-YYYY");
    },
    cleanEntityType(name) {
      if (!name) return "";
      const n = name.replaceAll("-", " ").replaceAll("_", " ");
      return n.charAt(0).toUpperCase() + n.slice(1);
    },
    documentParties(doc) {
      const parties = [];
      (doc.entityParties || []).forEach((party) => {
        parties.push({
          name: party.registeredName || "-",
          partyType: "Entity",
          identifier: party.brn || "-",
          contact: party.primaryPhone || party.primaryEmail || "-",
        });
      });
      (doc.individualParties || []).forEach((party) => {
        const name = [party.givenName, party.middleName, party.surName].filter(Boolean).join(" ");
        parties.push({
          name: name || "-",
          partyType: "Individual",
          identifier: party.idNumber || "-",
          contact: party.phoneNumber || party.email || "-",
        });
      });
      return parties;
    },
    documentIdOf(document) {
      return (
        document?.searchRequestDocumentId ||
        document?.searchDocumentId ||
        document?.id ||
        document?.documentId ||
        null
      );
    },
    retryPrompt(document, idx) {
      const searchDocumentId = this.documentIdOf(document);
      if (!searchDocumentId) {
        this.errorToast("Error", "Document id is missing for retry");
        return;
      }
      this.retryDocumentIdx = idx;
      this.retrySearchDocumentId = searchDocumentId;
      this.retryDocumentLabel = document.no || document.instrumentNumber || searchDocumentId;
      this.$refs.retryConfirm.show();
    },
    cancelRetry() {
      this.$refs.retryConfirm.hide();
      this.retryDocumentIdx = null;
      this.retrySearchDocumentId = null;
      this.retryDocumentLabel = null;
      this.loadingRetry = false;
    },
    async confirmRetry() {
      if (!this.trackingNumber || !this.retrySearchDocumentId) {
        return;
      }
      this.loadingRetry = true;
      try {
        await api().post(
          `legal-documents/certification/re-certify/document?trackingNo=${encodeURIComponent(this.trackingNumber)}&searchDocumentId=${encodeURIComponent(this.retrySearchDocumentId)}`
        );
        this.$refs.retryConfirm.hide();
        this.successToast(
          "Success",
          "Document certification retry initiated for " + (this.retryDocumentLabel || this.retrySearchDocumentId)
        );
        this.retryDocumentIdx = null;
        this.retrySearchDocumentId = null;
        this.retryDocumentLabel = null;
      } catch (error) {
        this.errorToast(
          "Error",
          error.response?.data?.message || "Failed to retry document certification"
        );
      } finally {
        this.loadingRetry = false;
      }
    },
  },
  watch: {
    trackingNumber() {
      this.fetch();
    },
  },
};
</script>

<style scoped>
th, td {
  font-size: 12px;
}
</style>
